import { BreakpointObserver } from '@angular/cdk/layout';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTableDataSource } from '@angular/material/table';
import { DISABLED_KPI_CLICKS, STUDY_KPI_TABLE_LABEL_MAP } from '@catalogues';
import {
  KpiCategories,
  KpiKeys,
  KpisByKey,
  KpiTableColumn,
  KpiTableRow,
  MilestoneName
} from '@models';
import { SelectedKpiService, SidebarService, SidePanelStateService } from '@services';
import { KpiCommonFunctionsService } from 'app/services/kpi-common-functions/kpi-common-functions.service';
import { auditTime, combineLatest, distinctUntilChanged, map } from 'rxjs';

@Component({
  selector: 'kpis-table-view',
  templateUrl: './kpis-table-view.component.html',
  styleUrl: './kpis-table-view.component.scss'
})
export class KpisTableViewComponent implements OnChanges, OnInit, OnDestroy {
  private kpiCommonFunctionsService: KpiCommonFunctionsService = inject(KpiCommonFunctionsService);
  private sidePanelService: SidePanelStateService = inject(SidePanelStateService);
  private sidebarService: SidebarService = inject(SidebarService);
  private selectedKpiService: SelectedKpiService = inject(SelectedKpiService);
  private breakpointObserver: BreakpointObserver = inject(BreakpointObserver);
  private destroyRef: DestroyRef = inject(DestroyRef);

  private NO_CHANGE_TREND_TEXT = 'No change since 30 days ago';
  private NO_TREND_DATA_TEXT = 'No trend data available';
  private NO_CHANGE_TREND_TEXT_SHORT = 'No change';
  private NO_TREND_DATA_TEXT_SHORT = 'No data';

  private selectedKpi?: KpiKeys;
  private sidebarExpanded!: boolean;
  private sidepanelExpanded!: boolean;
  private viewportBreakpoints: { below1610: boolean; below1320: boolean } = {
    below1610: false,
    below1320: false
  };

  @Input() kpisByCategory!: { category: KpiCategories; kpis: KpisByKey }[];
  @Input() keyMilestones?: MilestoneName[];

  private tooltipHideTimeoutId: number | null = null;
  private isRowHovered = false;
  private isTooltipHovered = false;
  private cursorX = 0;
  private cursorY = 0;
  private virtualOriginElement?: HTMLElement;
  private rowElement?: HTMLElement;

  data: Partial<Record<KpiCategories, KpiTableRow[]>> = {};
  columns: KpiTableColumn[] = [];
  columnKeys: (keyof KpiTableRow)[] = [];

  // Tooltip state management
  tooltipOpen = false;
  hoveredRow?: KpiTableRow;
  tooltipOriginElement?: CdkOverlayOrigin;

  hideBadgeLabel!: boolean;

  ngOnInit() {
    (Object.entries(STUDY_KPI_TABLE_LABEL_MAP) as [keyof KpiTableRow, string][]).forEach(
      ([key, label]) => {
        this.columnKeys.push(key);

        this.columns.push({
          key: key,
          label: label
        });
      }
    );

    this.selectedKpiService.selectedKpi$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((key) => {
        this.selectedKpi = key;
        this.handleSelectedKpiChange();
      });

    const sidePanel$ = this.sidePanelService.sidePanelExpanded$.pipe(distinctUntilChanged());
    const sidebarCollapsed$ = this.sidebarService.sidebarCollapsed$.pipe(distinctUntilChanged());
    const breakpoints$ = this.breakpointObserver
      .observe(['(max-width: 1610px)', '(max-width: 1320px)'])
      .pipe(
        map((result) => ({
          below1610: result.breakpoints['(max-width: 1610px)'],
          below1320: result.breakpoints['(max-width: 1320px)']
        })),
        distinctUntilChanged((a, b) => a.below1610 === b.below1610 && a.below1320 === b.below1320)
      );

    combineLatest([sidePanel$, sidebarCollapsed$, breakpoints$])
      .pipe(auditTime(0), takeUntilDestroyed(this.destroyRef))
      .subscribe(([sidePanelExpanded, sidebarCollapsed, breakpoints]) => {
        this.sidepanelExpanded = sidePanelExpanded;
        this.sidebarExpanded = !sidebarCollapsed;
        this.viewportBreakpoints = {
          below1610: breakpoints.below1610,
          below1320: breakpoints.below1320
        };
        this.hideBadgeLabel = this.breakpointConditionMet('below1610', false);
        this.handleTrendText();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['kpisByCategory']) {
      this.kpisByCategory.forEach((kpiByCategory) => {
        this.data[kpiByCategory.category] = Object.entries(kpiByCategory.kpis).map(
          ([key, kpi]) => ({
            name: kpi.name,
            type: kpi.type,
            key: key as KpiKeys,
            status: kpi.health,
            isSelected: this.selectedKpi === key,
            value: {
              displayValue: this.kpiCommonFunctionsService.getKpiDisplayValue(kpi, key as KpiKeys),
              displayUnit: this.kpiCommonFunctionsService.getKpiDisplayUnit(kpi.type),
              displaySecondaryValue: this.kpiCommonFunctionsService.getKpiDisplaySecondaryValue(
                kpi,
                key as KpiKeys
              )
            },
            trend: {
              trendText: this.getBreakpointTrendText(kpi.trendText, kpi.trendText),
              preSlicedTrendText: kpi.trendText,
              trendIndicator: kpi.trendIndicator,
              trendChange: kpi.trendChange
            }
          })
        );
      });
    }
  }

  getDataSource(kpiCategory: KpiCategories) {
    return new MatTableDataSource<KpiTableRow>(this.data[kpiCategory]);
  }

  isValid(value: string | number | undefined): boolean {
    return value !== undefined;
  }

  rowClicked(row: KpiTableRow) {
    if (!this.isValid(row.value.displayValue)) return;
    if (DISABLED_KPI_CLICKS.includes(row.key)) {
      return;
    }
    this.selectedKpiService.setSelectedKpi(row.isSelected ? undefined : row.key);
  }

  getColumnClass(key: keyof KpiTableRow): string | undefined {
    switch (key) {
      case 'name':
        return this.breakpointConditionMet('below1320')
          ? 'mat-column-kpi-ellipsis-name'
          : 'mat-column-kpi-name';
      case 'status':
        return this.hideBadgeLabel ? 'mat-column-status-no-label' : 'mat-column-status';
      case 'trend':
        return this.breakpointConditionMet('below1610') ? 'mat-column-kpi-trend' : undefined;
      default:
        return;
    }
  }

  private handleSelectedKpiChange() {
    let currentSelectedRow: KpiTableRow | undefined;
    let prevSelectedRow: KpiTableRow | undefined;
    Object.values(this.data).forEach((rows) => {
      if (!currentSelectedRow) currentSelectedRow = rows.find((r) => r.key === this.selectedKpi);
      if (!prevSelectedRow) prevSelectedRow = rows.find((r) => r.isSelected);
    });

    if (prevSelectedRow) prevSelectedRow.isSelected = false;

    if (currentSelectedRow) currentSelectedRow.isSelected = true;
  }

  private handleTrendText(): void {
    const breakpointCondition = this.breakpointConditionMet('below1610');
    Object.values(this.data).forEach((rows) => {
      rows.forEach((row) => {
        row.trend.trendText = this.getBreakpointTrendText(
          row.trend.trendText,
          row.trend.preSlicedTrendText,
          breakpointCondition
        );
      });
    });
  }

  private getBreakpointTrendText(
    trendText: string | undefined,
    preSlicedTrendText: string | undefined,
    breakpointCondition: boolean | undefined = undefined
  ): string | undefined {
    if (breakpointCondition === undefined) {
      breakpointCondition = this.breakpointConditionMet('below1610');
    }

    if (breakpointCondition) {
      switch (trendText) {
        case this.NO_CHANGE_TREND_TEXT_SHORT:
        case this.NO_CHANGE_TREND_TEXT:
          return this.NO_CHANGE_TREND_TEXT_SHORT;
        case this.NO_TREND_DATA_TEXT_SHORT:
        case this.NO_TREND_DATA_TEXT:
          return this.NO_TREND_DATA_TEXT_SHORT;
        default:
          return '';
      }
    } else {
      if (trendText !== preSlicedTrendText) return preSlicedTrendText;
    }

    return trendText;
  }

  ngOnDestroy(): void {
    if (this.tooltipHideTimeoutId) {
      window.clearTimeout(this.tooltipHideTimeoutId);
    }
    this.cleanupVirtualOriginElement();
  }

  onRowMouseEnter(row: KpiTableRow, event: MouseEvent): void {
    this.isRowHovered = true;
    this.hoveredRow = row;
    this.rowElement = event.currentTarget as HTMLElement;

    // Create a virtual element at adjusted position (outside the row)
    this.updateCursorPosition(event);
    this.createVirtualOriginElement();

    if (this.tooltipHideTimeoutId) {
      window.clearTimeout(this.tooltipHideTimeoutId);
      this.tooltipHideTimeoutId = null;
    }

    this.tooltipOpen = true;
  }

  onRowMouseLeave(): void {
    this.isRowHovered = false;
    this.scheduleTooltipHide();
  }

  onTooltipHover(): void {
    this.isTooltipHovered = true;

    if (this.tooltipHideTimeoutId) {
      window.clearTimeout(this.tooltipHideTimeoutId);
      this.tooltipHideTimeoutId = null;
    }
  }

  onTooltipLeave(): void {
    this.isTooltipHovered = false;
    this.scheduleTooltipHide();
  }

  private scheduleTooltipHide(): void {
    if (this.tooltipHideTimeoutId) {
      window.clearTimeout(this.tooltipHideTimeoutId);
      this.tooltipHideTimeoutId = null;
    }

    // Don't hide if either row or tooltip is hovered
    if (this.isRowHovered || this.isTooltipHovered) {
      return;
    }

    this.tooltipHideTimeoutId = window.setTimeout(() => {
      this.tooltipOpen = false;
      this.hoveredRow = undefined;
      this.tooltipOriginElement = undefined;
      this.tooltipHideTimeoutId = null;
      this.cleanupVirtualOriginElement();
    }, 150);
  }

  private updateCursorPosition(event: MouseEvent): void {
    this.cursorX = event.clientX;
    this.cursorY = event.clientY;
  }

  private createVirtualOriginElement(): void {
    if (!this.virtualOriginElement) {
      this.virtualOriginElement = document.createElement('div');
      this.virtualOriginElement.style.position = 'fixed';
      this.virtualOriginElement.style.width = '1px';
      this.virtualOriginElement.style.height = '1px';
      this.virtualOriginElement.style.pointerEvents = 'none';
      this.virtualOriginElement.style.visibility = 'hidden';
      document.body.appendChild(this.virtualOriginElement);
    }

    // Position tooltip at the top of the row with cursor's horizontal position
    let adjustedY = this.cursorY;

    if (this.rowElement) {
      const rowRect = this.rowElement.getBoundingClientRect();
      // Always position at the top edge of the row
      adjustedY = rowRect.top;
    }

    this.virtualOriginElement.style.left = `${this.cursorX}px`;
    this.virtualOriginElement.style.top = `${adjustedY}px`;

    this.tooltipOriginElement = new CdkOverlayOrigin(new ElementRef(this.virtualOriginElement));
  }

  private cleanupVirtualOriginElement(): void {
    if (this.virtualOriginElement) {
      document.body.removeChild(this.virtualOriginElement);
      this.virtualOriginElement = undefined;
    }
  }
  private breakpointConditionMet(
    breakpoint: 'below1610' | 'below1320',
    singlePanel = true
  ): boolean {
    return (
      this.viewportBreakpoints[breakpoint] &&
      (singlePanel
        ? this.sidepanelExpanded || this.sidebarExpanded
        : this.sidepanelExpanded && this.sidebarExpanded)
    );
  }
}
